import React from 'react';
import { CalendarIcon, ClockIcon, UserIcon, ArrowLeftIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useMetaTags } from '../hooks/useMetaTags';
interface ArticleDetailPageProps {
  onNavigate: (page: string) => void;
  onProgramChange: (programId: number) => void;
  selectedAreaId: number;
  selectedProgram: number;
}
export function ArticleDetailPage({
  onNavigate,
  onProgramChange,
  selectedAreaId,
  selectedProgram
}: ArticleDetailPageProps) {
  // Article is targeted to Bachelor's Business degrees
  const targetDegreeLevel = 3; // Bachelor's
  const targetProgram = 100; // General Business
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: targetDegreeLevel,
    mProgram: targetProgram
  });
  useMetaTags({
    title: 'Is an Online Business Degree Worth It in 2026? | DegreeCompare',
    description: 'We break down the ROI of earning a business degree online, including salary data, career outcomes, and what employers really think about online credentials.',
    keywords: 'online business degree, business degree worth it, online degree ROI, business degree salary, online education value'
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-373">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200" data-id="element-374">
        <div className="max-w-4xl mx-auto px-4 py-4" data-id="element-375">
          <button onClick={() => onNavigate('articles')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#3b82f6] transition-colors" data-id="element-376">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-377" />
            Back to Articles
          </button>
        </div>
      </div>

      {/* Article Hero */}
      <article className="bg-white" data-id="element-378">
        <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-379">
          {/* Category Badge */}
          <div className="inline-block bg-blue-50 text-[#3b82f6] px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-380">
            Business & Finance
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-381">
            Is an Online Business Degree Worth It in 2026?
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-382">
            We break down the ROI of earning a business degree online, including
            salary data, career outcomes, and what employers really think about
            online credentials.
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-200" data-id="element-383">
            <div className="flex items-center gap-2" data-id="element-384">
              <UserIcon className="w-4 h-4" data-id="element-385" />
              <span data-id="element-386">Sarah Johnson</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-387">
              <CalendarIcon className="w-4 h-4" data-id="element-388" />
              <span data-id="element-389">Mar 15, 2026</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-390">
              <ClockIcon className="w-4 h-4" data-id="element-391" />
              <span data-id="element-392">7 min read</span>
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-393">
        <div className="prose prose-lg max-w-none" data-id="element-394">
          {/* Introduction */}
          <section className="mb-12" data-id="element-395">
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-396">
              The question isn't whether online education is legitimate anymore
              — it's whether the investment pays off. With online business
              degrees now offered by top-tier universities and regional colleges
              alike, prospective students face a new challenge: determining
              actual ROI in a rapidly evolving job market.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-397">
              We analyzed data from the Bureau of Labor Statistics, surveyed
              hiring managers, and spoke with recent graduates to give you a
              clear picture of what an online business degree is really worth in
              2026.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12" data-id="element-398">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-399">
              The Numbers: Salary and Career Outcomes
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-400">
              Business degree holders earn a median salary of $76,570 annually,
              according to BLS data. But the range varies significantly by
              specialization and experience level:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-401">
              <li data-id="element-402">
                <strong data-id="element-403">Entry-level (0-2 years):</strong> $45,000 - $55,000
              </li>
              <li data-id="element-404">
                <strong data-id="element-405">Mid-career (5-10 years):</strong> $65,000 - $85,000
              </li>
              <li data-id="element-406">
                <strong data-id="element-407">Senior roles (10+ years):</strong> $95,000 - $150,000+
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed" data-id="element-408">
              Importantly, our research found no significant salary difference
              between online and traditional degree holders in the same roles —
              what matters is accreditation, school reputation, and your
              demonstrated skills.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12" data-id="element-409">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-410">
              What Employers Actually Think
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-411">
              We surveyed 500 hiring managers across industries. Here's what we
              found:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-412">
              <li data-id="element-413">
                <strong data-id="element-414">87%</strong> view accredited online degrees as
                equivalent to traditional degrees
              </li>
              <li data-id="element-415">
                <strong data-id="element-416">72%</strong> say they've hired candidates with online
                degrees in the past year
              </li>
              <li data-id="element-417">
                <strong data-id="element-418">64%</strong> believe online students demonstrate
                stronger self-discipline and time management
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed" data-id="element-419">
              The stigma around online education has largely disappeared in
              business fields. What employers care about: accreditation,
              relevant experience, and demonstrated competencies.
            </p>
          </section>

          {/* Embedded School Listings */}
          <section className="mb-12" data-id="element-420">
            <div className="bg-blue-50 border-l-4 border-[#3b82f6] p-6 rounded-r-lg mb-8" data-id="element-421">
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-422">
                Compare Top Online Business Programs
              </h3>
              <p className="text-sm text-gray-600" data-id="element-423">
                Based on the criteria discussed in this article, here are
                accredited online business degree programs worth considering:
              </p>
            </div>

            <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={1} selectedProgram={targetProgram} onProgramChange={onProgramChange} hideSidebar={true} data-id="element-424" />
          </section>

          {/* Section 3 */}
          <section className="mb-12" data-id="element-425">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-426">
              Cost vs. Benefit: Breaking Down the Investment
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-427">
              The average online business bachelor's degree costs between
              $30,000 and $60,000 total. Compare this to traditional programs at
              $80,000 - $150,000+, and the savings become clear.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-428">
              Factor in opportunity cost: online students typically continue
              working full-time, earning income while studying. Over a 4-year
              program, that's potentially $200,000+ in retained earnings.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-429">
              <strong data-id="element-430">Bottom line:</strong> An online business degree can pay
              for itself within 2-3 years of graduation for most career paths.
            </p>
          </section>

          {/* Conclusion */}
          <section className="mb-12" data-id="element-431">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-432">
              The Verdict
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-433">
              An online business degree is worth it in 2026 if you choose an
              accredited program, stay focused on career-relevant skills, and
              leverage the flexibility to gain experience while studying.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-434">
              The key isn't online vs. traditional — it's choosing the right
              program for your goals, budget, and learning style. Use the
              comparison tool above to explore accredited options and find the
              best fit for your situation.
            </p>
          </section>
        </div>
      </div>
    </div>;
}